import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { X, Search, Filter, Calendar, SlidersHorizontal } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

export default function DocumentFilters({ 
  onFilterChange, 
  totalCount, 
  filteredCount,
  documents = []
}) {
  const [filters, setFilters] = useState({
    search: '',
    status: [],
    source: [],
    apiSource: [],
    dateFrom: '',
    dateTo: '',
    expiryFrom: '',
    expiryTo: '',
    sortBy: 'newest',
    fileType: []
  });

  const [showAdvanced, setShowAdvanced] = useState(false);

  // Extract unique values for filters
  const uniqueApiSources = [...new Set(
    documents
      .filter(d => d.purpose?.includes('Fetched from'))
      .map(d => {
        const match = d.purpose.match(/Fetched from (.+) API/);
        return match ? match[1] : null;
      })
      .filter(Boolean)
  )];

  const uniqueFileTypes = [...new Set(
    documents.map(d => d.file_type || 'Unknown')
  )];

  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    onFilterChange(newFilters);
  };

  const toggleArrayFilter = (key, value) => {
    const current = filters[key];
    const newValue = current.includes(value)
      ? current.filter(v => v !== value)
      : [...current, value];
    handleFilterChange(key, newValue);
  };

  const clearFilters = () => {
    const resetFilters = {
      search: '',
      status: [],
      source: [],
      apiSource: [],
      dateFrom: '',
      dateTo: '',
      expiryFrom: '',
      expiryTo: '',
      sortBy: 'newest',
      fileType: []
    };
    setFilters(resetFilters);
    onFilterChange(resetFilters);
  };

  const activeFilterCount = 
    (filters.search ? 1 : 0) +
    filters.status.length +
    filters.source.length +
    filters.apiSource.length +
    filters.fileType.length +
    (filters.dateFrom || filters.dateTo ? 1 : 0) +
    (filters.expiryFrom || filters.expiryTo ? 1 : 0);

  const statusOptions = [
    { value: 'active', label: 'Active', color: '#10b981' },
    { value: 'locked', label: 'Locked', color: '#ef4444' },
    { value: 'expired', label: 'Expired', color: '#6b7280' }
  ];

  const sourceOptions = [
    { value: 'api', label: 'API Fetched', icon: '📥' },
    { value: 'manual', label: 'Manual Upload', icon: '📤' }
  ];

  return (
    <div className="space-y-4">
      {/* Search Bar */}
      <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
        <div className="flex-1 min-w-0 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
          <Input
            placeholder="Search by filename, UDT ID, or purpose..."
            value={filters.search}
            onChange={(e) => handleFilterChange('search', e.target.value)}
            className="pl-10 w-full"
          />
        </div>
        
        <div className="flex gap-2 flex-wrap sm:flex-nowrap">
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" className="relative flex-1 sm:flex-none">
              <SlidersHorizontal className="w-4 h-4 mr-2" />
              Filters
              {activeFilterCount > 0 && (
                <Badge 
                  className="ml-2 px-1.5 py-0.5 text-xs"
                  style={{ background: '#3b82f6' }}
                >
                  {activeFilterCount}
                </Badge>
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[calc(100vw-2rem)] max-w-96 max-h-[80vh] overflow-y-auto" align="start" sideOffset={8}>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h4 className="font-semibold text-sm">Advanced Filters</h4>
                {activeFilterCount > 0 && (
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={clearFilters}
                    className="h-8 text-xs"
                  >
                    Clear All
                  </Button>
                )}
              </div>

              {/* Status Filter */}
              <div className="space-y-2">
                <Label className="text-sm font-semibold">Status</Label>
                <div className="space-y-2">
                  {statusOptions.map((option) => (
                    <div key={option.value} className="flex items-center space-x-2">
                      <Checkbox
                        id={`status-${option.value}`}
                        checked={filters.status.includes(option.value)}
                        onCheckedChange={() => toggleArrayFilter('status', option.value)}
                      />
                      <label
                        htmlFor={`status-${option.value}`}
                        className="text-sm cursor-pointer flex items-center gap-2"
                      >
                        <div 
                          className="w-2 h-2 rounded-full" 
                          style={{ background: option.color }}
                        />
                        {option.label}
                      </label>
                    </div>
                  ))}
                </div>
              </div>

              {/* Source Filter */}
              <div className="space-y-2">
                <Label className="text-sm font-semibold">Source Type</Label>
                <div className="space-y-2">
                  {sourceOptions.map((option) => (
                    <div key={option.value} className="flex items-center space-x-2">
                      <Checkbox
                        id={`source-${option.value}`}
                        checked={filters.source.includes(option.value)}
                        onCheckedChange={() => toggleArrayFilter('source', option.value)}
                      />
                      <label
                        htmlFor={`source-${option.value}`}
                        className="text-sm cursor-pointer"
                      >
                        {option.icon} {option.label}
                      </label>
                    </div>
                  ))}
                </div>
              </div>

              {/* API Source Filter */}
              {uniqueApiSources.length > 0 && (
                <div className="space-y-2">
                  <Label className="text-sm font-semibold">API Source</Label>
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {uniqueApiSources.map((source) => (
                      <div key={source} className="flex items-center space-x-2">
                        <Checkbox
                          id={`api-${source}`}
                          checked={filters.apiSource.includes(source)}
                          onCheckedChange={() => toggleArrayFilter('apiSource', source)}
                        />
                        <label
                          htmlFor={`api-${source}`}
                          className="text-sm cursor-pointer"
                        >
                          {source}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* File Type Filter */}
              <div className="space-y-2">
                <Label className="text-sm font-semibold">File Type</Label>
                <div className="space-y-2">
                  {uniqueFileTypes.map((type) => (
                    <div key={type} className="flex items-center space-x-2">
                      <Checkbox
                        id={`type-${type}`}
                        checked={filters.fileType.includes(type)}
                        onCheckedChange={() => toggleArrayFilter('fileType', type)}
                      />
                      <label
                        htmlFor={`type-${type}`}
                        className="text-sm cursor-pointer"
                      >
                        {type}
                      </label>
                    </div>
                  ))}
                </div>
              </div>

              {/* Created Date Range */}
              <div className="space-y-2">
                <Label className="text-sm font-semibold flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  Created Date Range
                </Label>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs text-gray-500">From</Label>
                    <Input
                      type="date"
                      value={filters.dateFrom}
                      onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-gray-500">To</Label>
                    <Input
                      type="date"
                      value={filters.dateTo}
                      onChange={(e) => handleFilterChange('dateTo', e.target.value)}
                    />
                  </div>
                </div>
              </div>

              {/* Expiry Date Range */}
              <div className="space-y-2">
                <Label className="text-sm font-semibold flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  Expiry Date Range
                </Label>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs text-gray-500">From</Label>
                    <Input
                      type="date"
                      value={filters.expiryFrom}
                      onChange={(e) => handleFilterChange('expiryFrom', e.target.value)}
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-gray-500">To</Label>
                    <Input
                      type="date"
                      value={filters.expiryTo}
                      onChange={(e) => handleFilterChange('expiryTo', e.target.value)}
                    />
                  </div>
                </div>
              </div>
            </div>
          </PopoverContent>
        </Popover>

        <Select value={filters.sortBy} onValueChange={(v) => handleFilterChange('sortBy', v)}>
          <SelectTrigger className="w-full sm:w-48 min-w-0">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="newest">Newest First</SelectItem>
            <SelectItem value="oldest">Oldest First</SelectItem>
            <SelectItem value="name-asc">Name (A-Z)</SelectItem>
            <SelectItem value="name-desc">Name (Z-A)</SelectItem>
            <SelectItem value="expiry-soon">Expiring Soon</SelectItem>
            <SelectItem value="expiry-later">Expiring Later</SelectItem>
          </SelectContent>
        </Select>
        </div>
      </div>

      {/* Active Filters Display */}
      {activeFilterCount > 0 && (
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm text-gray-600">Active filters:</span>
          
          {filters.search && (
            <Badge variant="outline" className="gap-1">
              Search: {filters.search}
              <X 
                className="w-3 h-3 cursor-pointer" 
                onClick={() => handleFilterChange('search', '')}
              />
            </Badge>
          )}
          
          {filters.status.map(status => (
            <Badge key={status} variant="outline" className="gap-1">
              Status: {status}
              <X 
                className="w-3 h-3 cursor-pointer" 
                onClick={() => toggleArrayFilter('status', status)}
              />
            </Badge>
          ))}
          
          {filters.source.map(source => (
            <Badge key={source} variant="outline" className="gap-1">
              {source === 'api' ? '📥 API' : '📤 Manual'}
              <X 
                className="w-3 h-3 cursor-pointer" 
                onClick={() => toggleArrayFilter('source', source)}
              />
            </Badge>
          ))}
          
          {filters.apiSource.map(source => (
            <Badge key={source} variant="outline" className="gap-1">
              API: {source}
              <X 
                className="w-3 h-3 cursor-pointer" 
                onClick={() => toggleArrayFilter('apiSource', source)}
              />
            </Badge>
          ))}
          
          {filters.fileType.map(type => (
            <Badge key={type} variant="outline" className="gap-1">
              Type: {type}
              <X 
                className="w-3 h-3 cursor-pointer" 
                onClick={() => toggleArrayFilter('fileType', type)}
              />
            </Badge>
          ))}
          
          {(filters.dateFrom || filters.dateTo) && (
            <Badge variant="outline" className="gap-1">
              Created: {filters.dateFrom || '...'} to {filters.dateTo || '...'}
              <X 
                className="w-3 h-3 cursor-pointer" 
                onClick={() => {
                  handleFilterChange('dateFrom', '');
                  handleFilterChange('dateTo', '');
                }}
              />
            </Badge>
          )}
          
          {(filters.expiryFrom || filters.expiryTo) && (
            <Badge variant="outline" className="gap-1">
              Expiry: {filters.expiryFrom || '...'} to {filters.expiryTo || '...'}
              <X 
                className="w-3 h-3 cursor-pointer" 
                onClick={() => {
                  handleFilterChange('expiryFrom', '');
                  handleFilterChange('expiryTo', '');
                }}
              />
            </Badge>
          )}

          <Button 
            variant="ghost" 
            size="sm" 
            onClick={clearFilters}
            className="h-7 text-xs"
          >
            Clear All
          </Button>
        </div>
      )}

      {/* Results Count */}
      <div className="text-sm text-gray-600">
        Showing <span className="font-semibold">{filteredCount}</span> of{' '}
        <span className="font-semibold">{totalCount}</span> documents
      </div>
    </div>
  );
}
